import { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * The strip above the masthead.
 *
 * One message, one link, one way to close it. It states the shipping threshold
 * because that is the question a first-time shopper actually has before they
 * start browsing, and it links to the policy rather than restating it — the
 * threshold in the shipping policy is the one the checkout enforces.
 *
 * Dismissal persists, so a returning shopper is not told the same thing every
 * visit. It sits outside the sticky header so it scrolls away with the page.
 */

const STORAGE_KEY = 'ge:announcement-dismissed';
/** Bumped when the message changes, so a new one is shown to everyone again. */
const MESSAGE_ID = '2026-08-free-shipping';

const wasDismissed = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === MESSAGE_ID;
  } catch {
    // Private browsing throws on read. Showing the bar is the safe default.
    return false;
  }
};

export const AnnouncementBar = () => {
  const [visible, setVisible] = useState(() => !wasDismissed());

  if (!visible) return null;

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(STORAGE_KEY, MESSAGE_ID);
    } catch {
      // Losing the preference only means the bar returns next visit.
    }
  };

  return (
    <div className="relative bg-ink text-white">
      <div className="mx-auto flex max-w-(--container-content) items-center justify-center gap-x-3 gap-y-1 px-10 py-2.5 text-center sm:px-6">
        <p className="text-2xs leading-snug text-white/80">
          Free shipping on orders over ₹2,000. Seven-day returns on everything.{' '}
          <Link
            to="/shipping-and-delivery-policy"
            className="font-medium whitespace-nowrap text-white underline decoration-white/40 underline-offset-4 transition-colors hover:decoration-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            How delivery works
          </Link>
        </p>
      </div>

      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss announcement"
        className="absolute inset-y-0 right-1 grid w-9 place-items-center text-white/60 transition-colors hover:text-white focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-white sm:right-3"
      >
        <svg viewBox="0 0 16 16" className="size-3" fill="currentColor" aria-hidden="true">
          <path d="M4.3 3.24 8 6.94l3.7-3.7 1.06 1.06-3.7 3.7 3.7 3.7-1.06 1.06-3.7-3.7-3.7 3.7-1.06-1.06 3.7-3.7-3.7-3.7z" />
        </svg>
      </button>
    </div>
  );
};

export default AnnouncementBar;
